import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

const ReviewPrimaryActionContext = createContext(null);

const areEquivalentActionConfigs = (left, right) => {
  if (left === right) {
    return true;
  }

  if (!left || !right) {
    return false;
  }

  return left.id === right.id
    && left.label === right.label
    && Boolean(left.hidden) === Boolean(right.hidden)
    && Boolean(left.disabled) === Boolean(right.disabled)
    && Boolean(left.loading) === Boolean(right.loading)
    && Boolean(left.notReady) === Boolean(right.notReady)
    && (left.pendingReason || "") === (right.pendingReason || "");
};

export const ReviewPrimaryActionProvider = ({ children }) => {
  const [primaryAction, setPrimaryAction] = useState(null);
  const [backAction, setBackAction] = useState(null);

  const createActionRegistrar = useCallback((setter) => (actionConfig) => {
    if (!actionConfig?.id) {
      return () => { };
    }

    setter((currentAction) => (
      areEquivalentActionConfigs(currentAction, actionConfig)
        ? currentAction
        : actionConfig
    ));

    return () => {
      setter((currentAction) => (
        currentAction?.id === actionConfig.id ? null : currentAction
      ));
    };
  }, []);

  const registerPrimaryAction = useMemo(
    () => createActionRegistrar(setPrimaryAction),
    [createActionRegistrar]
  );

  const registerBackAction = useMemo(
    () => createActionRegistrar(setBackAction),
    [createActionRegistrar]
  );

  const contextValue = useMemo(() => ({
    backAction,
    primaryAction,
    registerBackAction,
    registerPrimaryAction,
  }), [backAction, primaryAction, registerBackAction, registerPrimaryAction]);

  return (
    <ReviewPrimaryActionContext.Provider value={contextValue}>
      {children}
    </ReviewPrimaryActionContext.Provider>
  );
};

export const useReviewPrimaryActionContext = () => useContext(ReviewPrimaryActionContext);

export default useReviewPrimaryActionContext;
